import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from '@/hooks/useInterval';
import { DataSource } from '@/models/dataSource';
import { Trait } from '@/models/traits';
import { useGetDataSourceStatsQuery } from '@/services/dataSources';
import { useWorkspace } from '@/features/workspaces/hooks';

import { DataSourceTypeCard, TrackedEventsCard, UniqueUsersByTraitCard } from './cards';
import { DEFAULT_STAT_TRAIT_ID } from './constant';
import { DataSourceDependencyAudiences } from './DataSourceDependencyAudiences';

type CommonActiveDataSourceProps = {
  dataSource: DataSource;
  polling: boolean;
};

export const CommonActiveDataSource: FC<CommonActiveDataSourceProps> = ({ dataSource, polling }) => {
  const workspace = useWorkspace();

  const { t } = useTranslation(['data_source_details', 'ALL']);

  const updatedAt = useRef<number>(0);
  const [updatedAgo, setUpdatedAgo] = useState<number>(0);
  const [userStatTrait, setUserStatTrait] = useState<Trait>();

  const {
    isFetching: isStatsFetching,
    isLoading: isStatsLoading,
    isSuccess: isStatsSuccess,
    isError: isStatsError,
    data: stats,
  } = useGetDataSourceStatsQuery(
    {
      workspaceId: workspace.id,
      dataSourceId: dataSource.id,
      traitId: userStatTrait?.id || DEFAULT_STAT_TRAIT_ID,
    },
    {
      pollingInterval: polling ? 300000 : 0,
    }
  );

  useEffect(() => {
    if (!isStatsFetching && (isStatsSuccess || isStatsError)) {
      updatedAt.current = Date.now();
      setUpdatedAgo(0);
    }
  }, [isStatsFetching, isStatsSuccess, isStatsError]);

  useInterval(() => {
    // Subtract 20 seconds so Math.round wil round up if it's 50 seconds or more
    const elapsedMillis = Date.now() - updatedAt.current - 20000;
    setUpdatedAgo(Math.abs(Math.round(elapsedMillis / 60000)));
  }, 10000);

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-2'>
        <div className='flex justify-between gap-4'>
          <TrackedEventsCard trackedEvents={stats?.events} isLoading={isStatsLoading} />

          <UniqueUsersByTraitCard
            dataSourceId={dataSource.id}
            userStatTrait={userStatTrait}
            uniqueUsers={stats?.users}
            isStatsFetching={isStatsFetching}
            onTraitChange={setUserStatTrait}
          />

          <DataSourceTypeCard dataSource={dataSource} isLoading={isStatsLoading} />
        </div>

        {polling && !isStatsLoading && (
          <div className='pl-2 text-xs text-quaternary-400'>
            {t(updatedAgo === 1 ? 'data_source_details:overview.reload_period_one' : 'data_source_details:overview.reload_period_other', { count: updatedAgo })}
          </div>
        )}
      </div>

      <DataSourceDependencyAudiences dataSource={dataSource} />
    </div>
  );
};
